// Consignment data for the screens: one summarizeCms() (shared/consignment.ts) over the synced tables, rebuilt
// only when one of them changes (or the hour turns, for counts coming due) and shared by every component that asks.
import { type CmData, type CmSummary, costLookup, type MakeOrderState, summarizeCms } from "../../../../shared/consignment.ts";
import { vendorCosts } from "../../../../shared/forecast.ts";
import { cmClaims } from "../../../../shared/presales.ts";
import { useBomIndex } from "../../lib/boms.ts";
import { useReservations } from "../../lib/presales.ts";
import { useRows } from "../../lib/sync.ts";

let last: { inputs: unknown[]; out: Map<string, CmSummary> } | null = null;

export function useCms(): Map<string, CmSummary> {
  const locations = useRows("location");
  const items = useRows("item");
  const balances = useRows("stock_balance");
  const levels = useRows("stock_level");
  const shipments = useRows("consign_shipment");
  const shipmentLines = useRows("consign_shipment_line");
  const makeOrders = useRows("make_order");
  const pos = useRows("po");
  const poLines = useRows("po_line");
  const reports = useRows("cm_report");
  const reportLines = useRows("cm_report_line");
  const counts = useRows("cm_count");
  const vendorItems = useRows("vendor_item");
  const ix = useBomIndex();
  const reservations = useReservations(); // Pre-sales --- what backers' boxes still take at each CM
  const hour = new Date().toISOString().slice(0, 13);
  const inputs = [
    locations,
    items,
    balances,
    levels,
    shipments,
    shipmentLines,
    makeOrders,
    pos,
    poLines,
    reports,
    reportLines,
    counts,
    vendorItems,
    ix,
    hour,
    reservations,
  ];
  if (!last || last.inputs.some((x, i) => x !== inputs[i])) {
    const data: CmData = {
      locations,
      items,
      balances,
      levels,
      shipments,
      shipmentLines,
      makeOrders,
      pos,
      poLines,
      reports,
      reportLines,
      counts,
      costOf: costLookup(vendorCosts(vendorItems), ix),
      claims: cmClaims(reservations),
    };
    last = { inputs, out: summarizeCms(data) };
  }
  return last.out;
}

/** CM sites by name. */
export const cmList = (cms: Map<string, CmSummary>) => [...cms.values()].sort((a, b) => a.location.name.localeCompare(b.location.name));

/** Every make order's state, by make order id. */
export function makeOrderStates(cms: Map<string, CmSummary>): Map<string, MakeOrderState> {
  const out = new Map<string, MakeOrderState>();
  for (const c of cms.values()) for (const o of c.orders) out.set(o.mo.id, o);
  return out;
}

export const STATUS_LABELS: Record<MakeOrderState["status"], string> = {
  ready: "Ready",
  arriving: "Ready when shipments arrive",
  short: "Short",
  built: "Built",
  closed: "Closed",
};
export const STATUS_TONES = { ready: "ok", arriving: "accent", short: "warn", built: "neutral", closed: "neutral" } as const;

/** $1,234 for dashboard totals. */
export const dollars = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const consignmentPath = (cmId: string) => `/consignment?cm=${cmId}`;
